import React, { useContext, useEffect, useRef, useState } from 'react';
import cx from 'classnames';
import * as PropTypes from 'prop-types';

import { PatientApi } from '../../../../../../../../api';
import ModalBoxes from '../../../../../../../../common/feedback/ModalBoxes/ModalBoxes';
import Button from '../../../../../../../../common/general/Button';
import Icon from '../../../../../../../../common/general/Icon';
import NotificationManager from '../../../../../../../../common/notifications/NotificationManager';
import { SUBJECT_ID_UPDATED } from '../../../../../../../../constants/notificationMessages';
import { onRequestError } from '../../../../../../../../services/handlers';
import { PatientInfoContext } from '../../../PatientInfoContext';

import './SubjectIdColumn.scss';

export default function SubjectIdColumn({ patientSubjectId, ssuPatientId, granted, isAllowedToManage }) {
  const { setSubjectIdBySsuPatientId } = useContext(PatientInfoContext);
  const [subjectId, setSubjectId] = useState(patientSubjectId);
  const [edit, setEdit] = useState(false);
  const inputRef = useRef();

  useEffect(
    function() {
      setSubjectId(patientSubjectId);
    },
    [patientSubjectId]
  );

  useEffect(
    function() {
      if (edit) {
        inputRef.current.focus();
      } else {
        setSubjectId(patientSubjectId);
      }
    },
    [edit, patientSubjectId]
  );

  return (
    <div className="ish-table-cell subject-id-column">
      {(function() {
        if (!granted) {
          return <span className="subject-id-is-hidden">SubjectId is hidden</span>;
        }
        if (edit || patientSubjectId) {
          return (
            <div className={cx('subject-id-field', { 'subject-id-field-edit': edit })}>
              <input
                ref={inputRef}
                type="text"
                value={fixControlledValue(subjectId)}
                onChange={onChange}
                disabled={!edit}
                title={fixControlledValue(subjectId)}
              />
              <div className="subject-id-field-buttons">
                {edit ? (
                  <React.Fragment>
                    <Button size="h28" priority="low" onClick={beforeSave}>
                      <Icon>save</Icon>
                    </Button>
                    <Button size="h28" priority="low" onClick={cancelHandler}>
                      <Icon>close</Icon>
                    </Button>
                  </React.Fragment>
                ) : (
                  <Button
                    disabled={!isAllowedToManage}
                    size="h28"
                    priority="low"
                    onClick={editHandler}
                    className="subject-id-field-button-edit"
                  >
                    <Icon>edit</Icon>
                  </Button>
                )}
              </div>
            </div>
          );
        }

        return (
          <Button disabled={!isAllowedToManage} size="h28" priority="medium" onClick={editHandler}>
            <Icon>add</Icon>
          </Button>
        );
      })()}
    </div>
  );

  function editHandler() {
    setEdit(true);
  }

  function beforeSave() {
    const resolvedSubjectId = subjectId?.trim() || null;
    PatientApi.checkSubjectIdInCurrentStudy(ssuPatientId, resolvedSubjectId)
      .then(function({ data: { duplicateSubjectId } }) {
        if (duplicateSubjectId) {
          ModalBoxes.confirm({
            title: 'Add Duplicate Subject ID',
            content: (
              <>
                The Subject ID <b>{resolvedSubjectId}</b> is already being used by a patient at this Study Site. Are you
                sure you want to add it for this patient?
              </>
            ),
            confirmButton: 'Yes',
            cancelButton: 'No'
          })
            .then(saveHandler)
            .catch(cancelHandler);
          return;
        }
        saveHandler();
      })
      .catch(err => onRequestError(err));
  }

  function cancelHandler() {
    setEdit(false);
  }

  function saveHandler() {
    PatientApi.updateSubjectId(ssuPatientId, subjectId || '')
      .then(res => {
        if (res) {
          setEdit(false);
          setSubjectIdBySsuPatientId(ssuPatientId, String(res.data?.subjectId || ''));
          NotificationManager.success(SUBJECT_ID_UPDATED);
        }
      })
      .catch(err => onRequestError(err, { messageLocation: 'response.data.response' }));
  }

  function onChange({ target }) {
    setSubjectId(target.value);
  }
}

SubjectIdColumn.propTypes = {
  patientSubjectId: PropTypes.string,
  ssuPatientId: PropTypes.string,
  isAllowedToAddOrEdit: PropTypes.bool
};

function fixControlledValue(value) {
  if (typeof value === 'undefined' || value === null) {
    return '';
  }
  return value;
}
